// The scope selector above every settings screen: the org default or one installation. Moving to
// another scope reloads the screen, so while its sub-tab holds unsaved changes the move asks first
// (REQ-237's unsaved guard covers links and unload; this covers the selector).
import { useEffect } from "preact/hooks";
import { Field } from "../primitives/field";
import type { HelpId } from "../primitives/help";
import { UNSAVED_CHANGES_PROMPT, useUnsavedGuard } from "../primitives/subtabs";

const SCOPE_HELP: HelpId = "settings.scope";

export interface ScopeOption {
  /** "" is the org default; otherwise the installation id. */
  id: string;
  label: string;
}

export interface ScopeSelectorProps {
  installations: readonly ScopeOption[];
  /** The selected scope: "" (org default) or an installation id. */
  value: string;
  onChange(scope: string): void;
  /**
   * The screen's sub-tab holds unsaved changes: a change of scope asks
   * `window.confirm(UNSAVED_CHANGES_PROMPT)` first, and Cancel keeps the scope selected.
   */
  dirty?: boolean | undefined;
}

export function ScopeSelector({ installations, value, onChange, dirty = false }: ScopeSelectorProps) {
  return (
    <Field id="settings-scope" label="Scope" help={SCOPE_HELP} hint="Installations inherit the org default unless they override a field.">
      <select
        id="settings-scope"
        value={value}
        onChange={(event) => {
          const select = event.currentTarget;
          if (dirty && !window.confirm(UNSAVED_CHANGES_PROMPT)) {
            select.value = value;
            return;
          }
          onChange(select.value);
        }}
      >
        <option value="">Org default</option>
        {installations.map((installation) => (
          <option key={installation.id} value={installation.id}>
            {installation.label}
          </option>
        ))}
      </select>
    </Field>
  );
}

/**
 * A settings sub-tab's guard while its draft differs from what was loaded: `useUnsavedGuard(dirty)`
 * for links and unload, and `report(dirty)` to the screen above it, which hands it to its
 * `ScopeSelector`. Reports false when the sub-tab unmounts (a save's reload, another scope).
 */
export function useDraftGuard(dirty: boolean, report: ((dirty: boolean) => void) | undefined): void {
  useUnsavedGuard(dirty);
  useEffect(() => report?.(dirty), [dirty]);
  useEffect(() => () => report?.(false), []);
}
